import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from '../ui/LoadingSpinner';

const DEFAULT_ITEM_HEIGHT = 120;
const OVERSCAN = 4;

type VirtualScrollColumnProps<T> = {
  items: T[];
  itemHeight?: number;
  className?: string;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  getItemKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => ReactNode;
};

export function VirtualScrollColumn<T>({
  items,
  itemHeight = DEFAULT_ITEM_HEIGHT,
  className = '',
  hasMore,
  loadingMore,
  onLoadMore,
  getItemKey,
  renderItem,
}: VirtualScrollColumnProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(520);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const updateHeight = (): void => {
      setViewportHeight(element.clientHeight);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleScroll = useCallback((): void => {
    const element = scrollRef.current;
    if (!element) return;

    setScrollTop(element.scrollTop);

    if (
      hasMore
      && !loadingMore
      && element.scrollTop + element.clientHeight >= element.scrollHeight - itemHeight
    ) {
      onLoadMore();
    }
  }, [hasMore, itemHeight, loadingMore, onLoadMore]);

  const totalHeight = items.length * itemHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - OVERSCAN);
  const visibleCount = Math.ceil(viewportHeight / itemHeight) + OVERSCAN * 2;
  const endIndex = Math.min(items.length, startIndex + visibleCount);
  const offsetY = startIndex * itemHeight;

  return (
    <div
      ref={scrollRef}
      className={`min-h-0 flex-1 overflow-y-auto p-2 ${className}`}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div
          className="absolute left-0 right-0 flex flex-col gap-2"
          style={{ transform: `translateY(${offsetY}px)` }}
        >
          {items.slice(startIndex, endIndex).map((item, index) => (
            <div key={getItemKey(item, startIndex + index)} style={{ minHeight: itemHeight - 8 }}>
              {renderItem(item, startIndex + index)}
            </div>
          ))}
        </div>
      </div>
      {loadingMore ? (
        <div className="flex justify-center py-2">
          <LoadingSpinner />
        </div>
      ) : null}
    </div>
  );
}
